import { Injectable } from '@angular/core';
import { AuthDataStore } from '../../../auth-data/auth-data.store';
import { catchError, map, mergeMap, take, tap } from 'rxjs/operators';
import { AccountDataStore } from '../../../account-data/account-data.store';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { combineLatest, Observable, of } from 'rxjs';
import { AccountDataDispatcher } from '../../../account-data/account-data.dispatcher';
import { AuthHttpService } from '../../../auth-data/auth-http.service';
import { LocalizationService } from '../../../localization/localization.service';

@Injectable()
export class SetPhoneDialogService {
  accessToken;
  refreshToken;
  // oob === out of band
  // https://auth0.com/docs/api/authentication#multi-factor-authentication
  oobCode: string;
  lastOobCode: string;
  parsePhoneNumber: Promise<any>;

  constructor(
    private _httpClient: HttpClient,
    private _authDataStore: AuthDataStore,
    private _authHttpService: AuthHttpService,
    private _accountDataStore: AccountDataStore,
    private _accountDataDispatcher: AccountDataDispatcher,
    private _localizationService: LocalizationService
  ) {
    this.parsePhoneNumber = import('libphonenumber-js');
  }

  validatePhoneNumber(phoneNumber: string, country: string) {
    this.oobCode = null;
    this.lastOobCode = null;
    return combineLatest([this.parsePhoneNumber, this.getMfaToken()]).pipe(
      mergeMap(([parsePhoneNumber]) =>
        this._httpClient.post(
          `${environment.auth0Url}/mfa/associate`,
          {
            authenticator_types: ['oob'],
            oob_channels: ['sms'],
            phone_number:
              parsePhoneNumber.default(phoneNumber, country)?.number || '', // strip 0s
          },
          {
            params: {
              'Content-Type': 'application/json',
            },
            headers: {
              Authorization: 'Bearer ' + this.accessToken,
              'x-request-language': this._localizationService.getLocale(),
            },
          }
        )
      ),
      tap((response: any) => {
        this.lastOobCode = this.oobCode;
        this.oobCode = response.oob_code;
      })
    );
  }

  // TODO: check if expired before refetching
  getMfaToken(): Observable<any> {
    return combineLatest([
      this._authDataStore.mfaExchangeToken$,
      this._authDataStore.userId$,
    ]).pipe(
      mergeMap(([mfaExchangeToken, userId]) =>
        this._httpClient.post(
          `${environment.auth0Url}/oauth/token`,
          {
            grant_type: 'http://auth0.com/oauth/grant-type/password-realm',
            client_id: environment.auth0ClientId,
            client_secret: environment.auth0ClientSecret,
            audience: `${environment.auth0Url}/mfa/`,
            username: userId,
            password: mfaExchangeToken,
            scope: 'offline_access',
            realm: 'MFA-Exchange',
          },
          {
            params: { 'Content-Type': 'application/x-www-form-urlencoded' },
          }
        )
      ),
      tap((response: any) => {
        this.accessToken = response.access_token;
        this.refreshToken = response.refresh_token;
      }),
      take(1)
    );
  }

  confirmCode(code) {
    // TODO: refresh MFA token if expired
    return combineLatest(
      [this.oobCode, this.lastOobCode].filter(Boolean).map((oobCode) =>
        this._httpClient
          .post(
            `${environment.auth0Url}/oauth/token`,
            {
              grant_type: 'http://auth0.com/oauth/grant-type/mfa-oob',
              client_id: environment.auth0ClientId,
              client_secret: environment.auth0ClientSecret,
              mfa_token: this.accessToken,
              oob_code: oobCode,
              binding_code: code,
            },
            {
              params: { 'Content-Type': 'application/x-www-form-urlencoded' },
            }
          )
          .pipe(catchError((error) => of(error)))
      )
    ).pipe(
      map((responses) => {
        const successResponses = responses.filter((result) => !result.error);
        if (successResponses.length === 0) {
          throw responses[0];
        }
      })
    );
  }

  refreshPhone() {
    return this._authHttpService
      .post(`${environment.gatewayUrl}/as/user/refresh_mfa_phone_number`, {})
      .pipe(mergeMap(() => this._accountDataDispatcher.fetchUser()));
  }
}
